import {
  useCallback,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

import type {
  ViewerEntityId,
  ViewerPosition,
} from '../contracts/models';
import type { PanoramaTarget } from '../panorama/pannellum';

export type BaseViewerMode =
  | 'view'
  | 'measure'
  | 'photoIndex'
  | 'areaTags';

export interface ViewerControllerOptions<
  TMode extends string,
  TLayoutId extends ViewerEntityId,
  TPhotoId extends ViewerEntityId,
> {
  initialMode: TMode;
  initialLayoutId: TLayoutId;
  initialPhotoId?: TPhotoId | null;
  initialScale?: number;
  initialPosition?: ViewerPosition;
  minimumScale?: number;
  maximumScale?: number;
}

export interface ViewerControllerResult<
  TMode extends string,
  TLayoutId extends ViewerEntityId,
  TPhotoId extends ViewerEntityId,
> {
  mode: TMode;
  setMode: Dispatch<SetStateAction<TMode>>;
  scale: number;
  setScale: Dispatch<SetStateAction<number>>;
  position: ViewerPosition;
  setPosition: Dispatch<SetStateAction<ViewerPosition>>;
  isDragging: boolean;
  setIsDragging: Dispatch<SetStateAction<boolean>>;
  selectedLayoutId: TLayoutId;
  setSelectedLayoutId: Dispatch<SetStateAction<TLayoutId>>;
  isPanoramaOpen: boolean;
  setIsPanoramaOpen: Dispatch<SetStateAction<boolean>>;
  openedPhotoId: TPhotoId | null;
  setOpenedPhotoId: Dispatch<SetStateAction<TPhotoId | null>>;
  panoramaTarget: PanoramaTarget | null;
  setPanoramaTarget: Dispatch<SetStateAction<PanoramaTarget | null>>;
  resetViewport(): void;
  zoomBy(factor: number): void;
  openPanorama(photoId: TPhotoId, target?: PanoramaTarget | null): void;
  closePanorama(): void;
}

export const useViewerController = <
  TMode extends string = BaseViewerMode,
  TLayoutId extends ViewerEntityId = ViewerEntityId,
  TPhotoId extends ViewerEntityId = ViewerEntityId,
>({
  initialMode,
  initialLayoutId,
  initialPhotoId = null,
  initialScale = 1,
  initialPosition = { x: 0, y: 0 },
  minimumScale = 0.1,
  maximumScale = 10,
}: ViewerControllerOptions<
  TMode,
  TLayoutId,
  TPhotoId
>): ViewerControllerResult<TMode, TLayoutId, TPhotoId> => {
  const [mode, setMode] = useState(initialMode);
  const [scale, setScale] = useState(initialScale);
  const [position, setPosition] =
    useState<ViewerPosition>(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedLayoutId, setSelectedLayoutId] =
    useState(initialLayoutId);
  const [isPanoramaOpen, setIsPanoramaOpen] = useState(
    initialPhotoId != null,
  );
  const [openedPhotoId, setOpenedPhotoId] =
    useState<TPhotoId | null>(initialPhotoId);
  const [panoramaTarget, setPanoramaTarget] =
    useState<PanoramaTarget | null>(null);

  const resetViewport = useCallback(() => {
    setScale(initialScale);
    setPosition(initialPosition);
    setIsDragging(false);
  }, [
    initialPosition.x,
    initialPosition.y,
    initialScale,
  ]);

  const zoomBy = useCallback(
    (factor: number) => {
      setScale((current) =>
        Math.min(
          maximumScale,
          Math.max(minimumScale, current * factor),
        ),
      );
    },
    [maximumScale, minimumScale],
  );

  const openPanorama = useCallback(
    (photoId: TPhotoId, target: PanoramaTarget | null = null) => {
      setOpenedPhotoId(photoId);
      setPanoramaTarget(target);
      setIsPanoramaOpen(true);
    },
    [],
  );

  const closePanorama = useCallback(() => {
    setIsPanoramaOpen(false);
    setOpenedPhotoId(null);
    setPanoramaTarget(null);
  }, []);

  return {
    mode,
    setMode,
    scale,
    setScale,
    position,
    setPosition,
    isDragging,
    setIsDragging,
    selectedLayoutId,
    setSelectedLayoutId,
    isPanoramaOpen,
    setIsPanoramaOpen,
    openedPhotoId,
    setOpenedPhotoId,
    panoramaTarget,
    setPanoramaTarget,
    resetViewport,
    zoomBy,
    openPanorama,
    closePanorama,
  };
};
